import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Brain, WarningDiamond, VideoCamera, Users, ShieldWarning, Fire, Wine,
  HandGrabbing, PersonSimpleWalk, MapPin,
} from "@phosphor-icons/react";

const SEV = {
  critical: { ring: "border-[#FF3B30]", dot: "bg-[#FF3B30]", text: "text-[#FF3B30]", label: "CRITICAL" },
  high: { ring: "border-[#FF9F0A]", dot: "bg-[#FF9F0A]", text: "text-[#FF9F0A]", label: "HIGH" },
  medium: { ring: "border-[#0A84FF]", dot: "bg-[#0A84FF]", text: "text-[#3395FF]", label: "MEDIUM" },
  low: { ring: "border-white/15", dot: "bg-[#71717A]", text: "text-[#A1A1AA]", label: "LOW" },
};

const TYPE_ICON = {
  Theft: ShieldWarning, Vandalism: Fire, Fight: HandGrabbing, Aggression: HandGrabbing,
  Intoxication: Wine, Productivity: PersonSimpleWalk, Loitering: PersonSimpleWalk,
};

const CAPABILITIES = [
  { icon: Users, label: "Staff vs Customer" },
  { icon: ShieldWarning, label: "Theft — internal & customer" },
  { icon: HandGrabbing, label: "Fights & aggression" },
  { icon: Wine, label: "Intoxication signs" },
  { icon: PersonSimpleWalk, label: "Loitering & movement" },
  { icon: Fire, label: "Vandalism" },
];

export default function CommandBrain() {
  const [situations, setSituations] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const tick = () => api.get("/situations").then((r) => {
      setSituations(r.data.situations || []);
      setStaff(r.data.staff_positions || []);
      setLoaded(true);
    }).catch(() => {});
    tick();
    const t = setInterval(tick, 5000);
    return () => clearInterval(t);
  }, []);

  const staffByDept = staff.reduce((acc, s) => {
    (acc[s.department] = acc[s.department] || []).push(s);
    return acc;
  }, {});

  return (
    <div data-testid="command-brain" className="space-y-4">
      <div className="bg-[#121214] border border-white/10 p-4">
        <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2">
          <Brain size={18} weight="bold" className="text-[#BF5AF2]" /> Command Brain
          <span className="font-mono-data text-[9px] px-1.5 py-0.5 bg-[#BF5AF2]/15 text-[#BF5AF2] border border-[#BF5AF2]/40">CROSS-CAMERA</span>
        </h2>
        <p className="text-xs text-[#A1A1AA] leading-relaxed mt-1">
          One shared brain for every camera. Alerts happening close in time — even on different cameras — are linked into a single <span className="text-white">situation</span>, so a shoplifter moving aisle→exit or a fight seen by two cameras reads as one event.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          {CAPABILITIES.map(({ icon: Icon, label }) => (
            <span key={label} className="flex items-center gap-1.5 text-[10px] font-mono-data text-[#A1A1AA] bg-[#1A1A1E] border border-white/10 px-2 py-1">
              <Icon size={12} weight="bold" className="text-[#BF5AF2]" /> {label}
            </span>
          ))}
        </div>
      </div>

      <div className="bg-[#121214] border border-white/10 p-4" data-testid="brain-staff-map">
        <h3 className="font-display font-black uppercase tracking-tighter text-xs flex items-center gap-2 mb-3">
          <MapPin size={15} weight="bold" className="text-[#32D74B]" /> Who's where — live staff map
          <span className="font-mono-data text-[9px] text-[#71717A]">({staff.length} on floor)</span>
        </h3>
        {staff.length === 0 ? (
          <p className="font-mono-data text-xs text-[#71717A]">No tracked staff on the floor right now.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.entries(staffByDept).map(([dept, list]) => (
              <div key={dept} className="bg-[#1A1A1E] border border-white/10 p-2.5" data-testid={`dept-${dept}`}>
                <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#BF5AF2] mb-1.5">{dept}</p>
                {list.map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-[11px] text-[#E4E4E7] py-0.5">
                    <span className="truncate">{s.name}</span>
                    <span className="font-mono-data text-[9px] text-[#71717A] shrink-0">{s.camera}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {!loaded ? (
        <div className="text-center text-[#71717A] font-mono-data text-sm py-10">Loading situations…</div>
      ) : situations.length === 0 ? (
        <div data-testid="brain-empty" className="border border-white/10 p-10 text-center">
          <Brain size={48} weight="thin" className="text-[#32D74B] mx-auto mb-3" />
          <p className="font-display font-black uppercase tracking-tighter">All Quiet</p>
          <p className="font-mono-data text-xs text-[#71717A] mt-1">No active situations across your cameras. New events will cluster here live.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="brain-situations">
          {situations.map((s) => {
            const sev = SEV[s.severity] || SEV.low;
            return (
              <div key={s.id} data-testid={`situation-${s.id}`} className={`bg-[#121214] border-l-2 ${sev.ring} border-t border-r border-b border-white/10 p-4`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`flex items-center gap-2 font-display font-black uppercase tracking-tight text-sm ${sev.text}`}>
                    <span className={`w-2 h-2 rounded-full ${sev.dot} ${s.severity === "critical" ? "live-dot" : ""}`} /> {sev.label} SITUATION
                  </span>
                  <span className="font-mono-data text-[10px] text-[#71717A]">{s.count} alert{s.count > 1 ? "s" : ""}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {s.types.map((t) => {
                    const Icon = TYPE_ICON[t] || WarningDiamond;
                    return (
                      <span key={t} className="flex items-center gap-1 text-[10px] font-mono-data bg-[#1A1A1E] border border-white/10 px-1.5 py-0.5">
                        <Icon size={11} weight="fill" className={sev.text} /> {t}
                      </span>
                    );
                  })}
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-[#A1A1AA] mb-2">
                  <VideoCamera size={13} weight="bold" className="text-[#71717A]" />
                  {s.cameras.join(" · ")}
                </div>
                {(s.zones?.length > 0 || s.actors?.length > 0) && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {s.zones?.map((z) => (
                      <span key={z} className="flex items-center gap-1 text-[10px] font-mono-data bg-[#0A84FF]/15 text-[#3395FF] border border-[#0A84FF]/40 px-1.5 py-0.5">
                        <MapPin size={10} weight="fill" /> {z}
                      </span>
                    ))}
                    {s.actors?.map((ac) => (
                      <span key={ac} className={`flex items-center gap-1 text-[10px] font-mono-data px-1.5 py-0.5 border ${ac === "employee" ? "bg-[#FF9F0A]/15 text-[#FF9F0A] border-[#FF9F0A]/40" : "bg-[#FF375F]/15 text-[#FF6482] border-[#FF375F]/40"}`}>
                        <Users size={10} weight="fill" /> {ac === "employee" ? "Internal · staff" : ac === "customer" ? "Customer" : ac}
                      </span>
                    ))}
                  </div>
                )}
                <div className="space-y-1 border-t border-white/5 pt-2">
                  {s.alerts.map((a) => (
                    <p key={a.id} className="text-xs text-[#E4E4E7] leading-snug">
                      <span className={`font-mono-data ${sev.text}`}>▸</span> {a.message}
                    </p>
                  ))}
                </div>
                <p className="font-mono-data text-[10px] text-[#52525B] mt-2">
                  {new Date(s.started).toLocaleTimeString()} → {new Date(s.latest).toLocaleTimeString()}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
